import React, { useRef, useEffect, useState } from 'react';
import { Card, Button, message, Tooltip } from 'antd';
import { models } from 'powerbi-client';
import PropTypes from 'prop-types';
import useReportHeight from '../hooks/powerBI/useReportHeight';
import useFullscreen from '../hooks/powerBI/useFullscreen';
import useReportZoom from '../hooks/powerBI/useReportZoom';
import usePowerBiEmbed from '../hooks/powerBI/usePowerBiEmbed';
import PowerBIReportContent from './PowerBIReportContent';
import './css/powerBIReport.css';
import useUserType from '../hooks/useUserType';
import { ReloadOutlined } from '@ant-design/icons';

const PowerBIReport = ({ 
  workspaceId,  
  reportId,
  userId,
  endpoint,    
  filterPaneEnabled = false,
  navContentPaneEnabled = false 
}) => {
  const cardRef = useRef(null);
  const reportRef = useRef(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const reportHeight = useReportHeight();

  const userType = useUserType();
  const isAdmin = userType === 'administrador';

  const { updateZoom, updateZoomWithDelay } = useReportZoom(reportRef, cardRef);
  const { isFullscreen, toggleFullscreen } = useFullscreen(updateZoom);
  
  const {
    embedInfo,
    loading,
    error,
    fetchEmbedInfo,
    handleError,
    refreshReportWithLimit // <-- adicionado aqui
  } = usePowerBiEmbed(workspaceId, reportId, userId, endpoint);

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
      if (reportRef.current && cardRef.current) {
        updateZoomWithDelay(300);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFullscreen, updateZoomWithDelay]);

  useEffect(() => {
    if (containerDimensions.width > 0 && 
        containerDimensions.height > 0 && 
        reportRef.current && 
        cardRef.current) {
      updateZoomWithDelay(100);
    }
  }, [containerDimensions, updateZoomWithDelay]);

  const eventHandlers = [
    {
      eventName: 'loaded',
      handler: async function () {
        if (reportRef.current && cardRef.current) {
          await updateZoomWithDelay(500);
          await updateZoomWithDelay(1000);
          await updateZoomWithDelay(2000);
        }
      }
    },
    {
      eventName: 'rendered',
      handler: function () {
        if (reportRef.current && cardRef.current) {
          updateZoom();
        }
      }
    },
    {
      eventName: 'error',
      handler: async function (event) {
        await handleError(event, reportRef);
      }
    },
    {
      eventName: 'pageChanged',
      handler: function () {
        if (reportRef.current && cardRef.current) {
          updateZoomWithDelay(100);
          updateZoomWithDelay(500);
        }
      }
    }
  ];

  const reportConfig = embedInfo ? {
    type: 'report',
    id: embedInfo.reportInfo.id,
    embedUrl: embedInfo.reportInfo.embedUrl,
    accessToken: embedInfo.embedToken,
    tokenType: models.TokenType.Embed,
    permissions: models.Permissions.All,
    viewMode: models.ViewMode.View,
    settings: {
      panes: {
        filters: {
          expanded: false,
          visible: filterPaneEnabled
        },
        pageNavigation: {
          visible: navContentPaneEnabled
        }
      },
      background: models.BackgroundType.Transparent,
      layoutType: models.LayoutType.Master,
      customLayout: {
        displayOption: models.DisplayOption.ActualSize
      }
    }
  } : null;

  return (
    <Card 
      ref={cardRef}
      style={{ 
        width: '100%',
        height:  `100vh`,
        background: loading || error ? '#f0f2f5' : 'transparent',
        marginBottom: isFullscreen ? 0 : 0,
        position: 'relative',
        transition: 'height 0.3s ease-in-out',
        ...(isFullscreen && {
          borderRadius: 0,
          border: 'none'
        })
      }}
      bodyStyle={{ 
        height: '100%',
        padding: windowDimensions.width <= 768 ? '10px' : 0,
        overflow: 'hidden',
        background: 'transparent'
      }}
    >
      {/* Botão Atualizar Dados com limite de 5 por dia */}
      
      {isAdmin && (
      <Tooltip
        title={`Atualizar dados`}
      >
        <Button 
          type="primary"
          icon={<ReloadOutlined />}
          style={{
            position: 'absolute',
            top: 5,
            left: 40,
            zIndex: 100,
            backgroundColor: '#1677ff',
            borderColor: '#1677ff'
          }}
          onClick={refreshReportWithLimit}
          disabled={loading}
        />
      </Tooltip>
    )}





      <PowerBIReportContent
        loading={loading}
        error={error}
        embedInfo={embedInfo}
        reportConfig={reportConfig}
        eventHandlers={eventHandlers}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
        cardRef={cardRef}
        reportRef={reportRef}
        updateZoomWithDelay={updateZoomWithDelay}
        fetchEmbedInfo={fetchEmbedInfo}
        windowWidth={windowDimensions.width}
      />
    </Card>
  );
};

PowerBIReport.propTypes = {
  workspaceId: PropTypes.string.isRequired,
  reportId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  filterPaneEnabled: PropTypes.bool,
  navContentPaneEnabled: PropTypes.bool
};

PowerBIReport.defaultProps = {
  filterPaneEnabled: true,
  navContentPaneEnabled: true
};

export default PowerBIReport;
